import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { AlertTriangle, Check, Edit, IndianRupee } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { PAYROLL_ATTENDANCE_PENALTIES } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input } from "../../components/ui/input";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const { Option } = Select;

/**
 * Attendance Penalties — fines and deductions for single punch, late arrival, etc.
 * Pending rows can be accepted in one click; Edit is for overriding the suggested value.
 */
const AttendancePenalties = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const periodPreset = location.state;
    const { update } = useApiMethods();
    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Attendance Penalties", "SubMenu");
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee_name");

    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [tableFilters, setTableFilters] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [acceptLoadingId, setAcceptLoadingId] = useState(null);
    const [bulkAcceptLoading, setBulkAcceptLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState({
        mode: "deduction",
        deduction: 0.5,
        fine: 0,
    });

    const penaltyPeriodFilter = useMemo(() => {
        const filter = {};
        if (periodPreset?.penaltyType) filter.penalty_type = periodPreset.penaltyType;
        if (periodPreset?.reviewed) filter.reviewed = periodPreset.reviewed;
        return filter;
    }, [periodPreset]);

    const selectedDate = useMemo(() => {
        if (!periodPreset?.fromDate || !periodPreset?.toDate) return undefined;
        return {
            from_date: dayjs(periodPreset.fromDate),
            to_date: dayjs(periodPreset.toDate),
        };
    }, [periodPreset]);

    const resolveDateRange = useCallback(() => {
        const from_date = tableFilters.from_date || periodPreset?.fromDate;
        const to_date = tableFilters.to_date || periodPreset?.toDate;
        return { from_date, to_date };
    }, [tableFilters, periodPreset]);

    const resetForm = () => {
        setOpenDialog(false);
        setSelectedRow(null);
        setFormData({ mode: "deduction", deduction: 0.5, fine: 0 });
    };

    const handleOpenEdit = (row) => {
        setSelectedRow(row);
        setFormData({
            mode: row?.mode === "fine" ? "fine" : "deduction",
            deduction: Number(row?.deduction ?? 0.5),
            fine: Number(row?.fine ?? 0),
        });
        setOpenDialog(true);
    };

    const handleAccept = async (row) => {
        if (!row?.attendance_id) return;
        setAcceptLoadingId(row.attendance_id);
        try {
            const response = await update(PAYROLL_ATTENDANCE_PENALTIES, {
                action: "accept_suggested",
                attendance_ids: [row.attendance_id],
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Penalty accepted");
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to accept penalty");
            }
        } catch (error) {
            console.error(error);
            ToastMsg("error", "Failed to accept penalty");
        } finally {
            setAcceptLoadingId(null);
        }
    };

    const handleAcceptAllPending = async () => {
        const { from_date, to_date } = resolveDateRange();
        if (!from_date || !to_date) {
            ToastMsg("error", "Select a from/to date range in the table filters first");
            return;
        }
        setBulkAcceptLoading(true);
        try {
            const response = await update(PAYROLL_ATTENDANCE_PENALTIES, {
                action: "accept_suggested",
                from_date,
                to_date,
            });
            if (response?.status === "success") {
                const count = response?.data?.updated ?? 0;
                ToastMsg("success", count
                    ? `Accepted ${count} pending ${count === 1 ? "penalty" : "penalties"}`
                    : (response.message || "No pending penalties in this date range"));
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to accept penalties");
            }
        } catch (error) {
            console.error(error);
            ToastMsg("error", "Failed to accept penalties");
        } finally {
            setBulkAcceptLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedRow?.attendance_id) return;
        try {
            setLoading(true);
            const payload = {
                attendance_id: selectedRow.attendance_id,
                mode: formData.mode,
                deduction: formData.deduction,
                fine: formData.fine,
                penalty_type: selectedRow.penalty_type,
                is_late: selectedRow.is_late,
            };
            const response = await update(PAYROLL_ATTENDANCE_PENALTIES, payload);
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Penalty updated");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to update penalty");
            }
        } catch (error) {
            console.error(error);
            ToastMsg("error", "Failed to update penalty");
        } finally {
            setLoading(false);
        }
    };

    const EmployeeCell = ({ row }) => (
        <div className="flex items-center gap-2">
            <div>
                <div className="font-medium text-gray-900 dark:text-white">{row?.employee_name || "-"}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{row?.employee_code || "-"}</div>
            </div>
            {row?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
        </div>
    );

    const columns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee_name",
            Header: "Employee",
            fixed: true,
            filterable: true,
            maxWidth: "260px",
            Cell: EmployeeCell,
        },
        {
            accessor: "department_name",
            Header: "Department",
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "date",
            Header: "Date",
            sortable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "penalty_type",
            Header: "Type",
            filterable: true,
            Cell: ({ value }) => (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    {value || "-"}
                </span>
            ),
        },
        {
            accessor: "late_minutes",
            Header: "Late (min)",
            Cell: ({ value }) => (value > 0 ? value : "-"),
        },
        {
            accessor: "grace_status",
            Header: "Grace",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "penalty",
            Header: "Penalty",
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                        value === "Fine"
                            ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                            : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    )}
                >
                    {value || "-"}
                </span>
            ),
        },
        {
            accessor: "value",
            Header: "Value",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "review_status",
            Header: "Status",
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                        value === "Reviewed"
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    )}
                >
                    {value || "-"}
                </span>
            ),
        },
        {
            accessor: "reason",
            Header: "Reason",
            Cell: ({ value }) => value || "-",
        },
        ...(editPermission ? [{
            accessor: "action",
            Header: "Actions",
            sortable: false,
            fixed: true,
            align: "right",
            Cell: ({ row }) => {
                const isPending = row?.review_status !== "Reviewed";
                const isAccepting = acceptLoadingId === row?.attendance_id;
                return (
                    <div className="flex items-center justify-end gap-1">
                        {isPending && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                                disabled={isAccepting || bulkAcceptLoading}
                                onClick={() => handleAccept(row)}
                                title="Accept suggested penalty"
                            >
                                {isAccepting ? (
                                    <span className="h-4 w-4 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
                                ) : (
                                    <Check className="h-4 w-4" />
                                )}
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                            onClick={() => handleOpenEdit(row)}
                            title={isPending ? "Change penalty before accepting" : "Edit penalty"}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    </div>
                );
            },
        }] : []),
    ]), [editPermission, insertBranchColumn, showBranchColumn, TempTransferBadge, acceptLoadingId, bulkAcceptLoading]);

    return (
        <ContainerCard scrollable>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3 p-3 rounded-lg shrink-0">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <AlertTriangle className="w-6 h-6 text-sidebar-primary flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Attendance Penalties
                            {totalRecords !== undefined && totalRecords !== null && (
                                <span className="text-muted-foreground font-normal ml-2">
                                    ({totalRecords})
                                </span>
                            )}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Accept suggested penalties in one click, or edit to override before confirming
                        </p>
                    </div>
                </div>
                {periodPreset?.fromPayrollRun && periodPreset?.payrollId && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/payroll/runpayrollprocess', { state: { PaySlipId: periodPreset.payrollId } })}
                        className="shrink-0"
                    >
                        ← Back to payroll run
                    </Button>
                )}
                {editPermission && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={bulkAcceptLoading}
                        onClick={handleAcceptAllPending}
                        className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                    >
                        {bulkAcceptLoading ? (
                            <span className="h-4 w-4 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
                        ) : (
                            <Check className="h-4 w-4" />
                        )}
                        Accept all pending in date range
                    </Button>
                )}
            </div>

            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                setFetchFilter={setTableFilters}
                url={PAYROLL_ATTENDANCE_PENALTIES}
                filter={penaltyPeriodFilter}
                selectedDate={selectedDate}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? "sm" : null}
                isCSVRequired={true}
                isPDFRequired={true}
                isFromToDateRequired={true}
            />

            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onSave={handleSave}
                title="Override Attendance Penalty"
                closeLabel="Cancel"
                saveLabel="Confirm"
                editMode={true}
                loading={loading}
                width="440px"
            >
                <div className="space-y-4">
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {selectedRow?.employee_name || "-"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedRow?.date || "-"} · {selectedRow?.penalty_type || "-"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Adjust the value below, then confirm. Use Accept on the list when the suggested value is fine.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Penalty Mode
                        </label>
                        <Select
                            size="large"
                            className="w-full"
                            value={formData.mode}
                            onChange={(value) => setFormData((prev) => ({ ...prev, mode: value }))}
                        >
                            <Option value="deduction">Deduction (Loss of Pay)</Option>
                            <Option value="fine">Fine (Flat Amount)</Option>
                        </Select>
                    </div>

                    {formData.mode === "fine" ? (
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                                <IndianRupee className="h-3 w-3" /> Fine Amount
                            </label>
                            <Input
                                type="number"
                                min={0}
                                step={1}
                                value={formData.fine}
                                onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    fine: Number(e.target.value),
                                }))}
                            />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Day Fraction (0 = waive, 0.5 = half day, 1 = full day)
                            </label>
                            <Input
                                type="number"
                                min={0}
                                max={1}
                                step={0.25}
                                value={formData.deduction}
                                onChange={(e) => setFormData((prev) => ({
                                    ...prev,
                                    deduction: Number(e.target.value),
                                }))}
                            />
                        </div>
                    )}
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default AttendancePenalties;
